import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {AssetType} from '@gatewee/types/asset';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeAssetUploadComponent} from '../../gatewee-asset-upload/gatewee-asset-upload.component';

export interface MobileBuildManifestValue {
  appName?: string;
  slug?: string;
  scheme?: string;
  android?: {package?: string};
  ios?: {bundleIdentifier?: string};
  iconUrl?: string;
  splashImageUrl?: string;
  splash?: {backgroundColor?: string};
}

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  domainId?: number;
}

@Component({
  selector: 'mobile-build-manifest-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
    GateweeAssetUploadComponent,
  ],
  templateUrl: './mobile-build-manifest-parameter.component.html',
})
export class MobileBuildManifestParameterComponent implements OnInit {
  static readonly IMAGE_CONFIG: Record<
    string,
    {
      assetType: AssetType;
      aspectRatio: number;
      resizeToWidth?: number;
      resizeToHeight?: number;
    }
  > = {
    iconUrl: {
      assetType: 'brandingIcon',
      aspectRatio: 1,
      resizeToWidth: 1024,
      resizeToHeight: 1024,
    },
    splashImageUrl: {
      assetType: 'brandingLogo',
      aspectRatio: 1,
      resizeToWidth: 645,
      resizeToHeight: 645,
    },
  };

  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  domainId?: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  readonly imageConfig = MobileBuildManifestParameterComponent.IMAGE_CONFIG;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.domainId = this.context.data.domainId ?? this.context.data.id;
    this.setting = this.context.data.setting;
    this.editSettingFn = this.context.data.editSettingFn;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private parseValue(): MobileBuildManifestValue {
    const raw = this.setting.value;
    if (!raw) return {};
    try {
      const parsed =
        typeof raw === 'string'
          ? JSON.parse(raw)
          : (raw as MobileBuildManifestValue);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  private initializeForm(): void {
    const v = this.parseValue();
    this.formGroup = this.fb.group({
      appName: [v.appName ?? '', Validators.required],
      slug: [v.slug ?? '', Validators.required],
      scheme: [v.scheme ?? ''],
      androidPackage: [v.android?.package ?? ''],
      iosBundleIdentifier: [v.ios?.bundleIdentifier ?? ''],
      iconUrl: new FormControl<string | null>(v.iconUrl ?? null),
      splashImageUrl: new FormControl<string | null>(v.splashImageUrl ?? null),
      splashBackgroundColor: [v.splash?.backgroundColor ?? ''],
    });
  }

  getImageControl(field: string): FormControl<string | null> {
    return this.formGroup.get(field) as FormControl<string | null>;
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const f = this.formGroup.value;
    const value: MobileBuildManifestValue = {
      appName: f.appName || undefined,
      slug: f.slug || undefined,
      scheme: f.scheme || undefined,
      android: f.androidPackage ? {package: f.androidPackage} : undefined,
      ios: f.iosBundleIdentifier
        ? {bundleIdentifier: f.iosBundleIdentifier}
        : undefined,
      iconUrl: f.iconUrl || undefined,
      splashImageUrl: f.splashImageUrl || undefined,
      splash: f.splashBackgroundColor
        ? {backgroundColor: f.splashBackgroundColor}
        : undefined,
    };

    const updatedSetting = {
      ...this.setting,
      value,
    } as RecipientSetting | DomainSetting;

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating mobile build manifest:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
